import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  LogOut,
  Menu,
  Settings,
  X,
} from 'lucide-react';
import clsx from 'clsx';
import type { AppShellProps, NavigationItem } from '../../types/ui';
import { useLanguage } from '../../contexts/LanguageContext';
import { NotificationsBell } from '../notifications';
import { ThemeToggle } from '../ui/ThemeToggle';
import LanguageToggle from '../ui/LanguageToggle';
import ClassTechLogo from '../brand/ClassTechLogo';

const BrandMark: React.FC = () => (
  <ClassTechLogo variant='white' size='sm' showTagline />
);

const AppShell: React.FC<AppShellProps> = ({
  children,
  navigation,
  activeItemId,
  onNavigate,
  userName,
  userEmail,
  userRoleLabel,
  pageTitle,
  pageDescription,
  actions,
  notice,
  sidebarFooter,
  onLogout,
}) => {
  const navigate = useNavigate();
  const { isRTL } = useLanguage();
  const { t } = useTranslation(['common', 'navigation']);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const closeProfile = (event: MouseEvent) => {
      if (
        profileRef.current &&
        !profileRef.current.contains(event.target as Node)
      ) {
        setProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', closeProfile);
    return () => document.removeEventListener('mousedown', closeProfile);
  }, []);

  const groupedNavigation = useMemo(() => {
    const groups = new Map<string, NavigationItem[]>();
    navigation.forEach(item => {
      const group = item.group || t('navigation:main');
      groups.set(group, [...(groups.get(group) || []), item]);
    });
    return Array.from(groups.entries());
  }, [navigation, t]);

  const mobileNavigation = useMemo(() => {
    const preferred = navigation.filter(item => item.mobile);
    return (preferred.length > 0 ? preferred : navigation).slice(0, 5);
  }, [navigation]);

  const handleNavigation = (item: NavigationItem) => {
    setMobileOpen(false);
    if (item.href) {
      navigate(item.href);
      return;
    }
    onNavigate(item);
  };

  const notificationsItem = navigation.find(item => item.id === 'notifications');

  const sidebar = (
    <div className='flex h-full flex-col'>
      <div className='flex h-20 items-center border-b border-white/10 px-5'>
        <BrandMark />
      </div>

      <div className='border-b border-white/10 px-4 py-4'>
        <div className={clsx('flex w-full items-center gap-3 rounded-xl p-2 text-white', isRTL ? 'text-right' : 'text-left')}>
          <span className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-600 font-bold text-white ring-2 ring-cyan-300/50'>
            {userName.trim().charAt(0) || 'م'}
          </span>
          <span className='min-w-0 flex-1'>
            <span className='block truncate text-sm font-semibold'>{userName}</span>
            <span className='block truncate text-xs text-secondary-300'>
              {userRoleLabel}
            </span>
          </span>
        </div>
      </div>

      <nav
        className='flex-1 overflow-y-auto px-3 py-4'
        aria-label={t('navigation:main')}
      >
        {groupedNavigation.map(([group, items]) => (
          <div key={group} className='mb-5'>
            <p className='mb-2 px-3 text-xs font-semibold text-secondary-400'>
              {group}
            </p>
            <div className='space-y-1'>
              {items.map(item => {
                const Icon = item.icon;
                const active = item.id === activeItemId;
                return (
                  <button
                    type='button'
                    key={item.id}
                    onClick={() => handleNavigation(item)}
                    className={clsx(
                      'flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300',
                      active
                        ? 'bg-primary-600 text-white shadow-sm'
                        : 'text-secondary-300 hover:bg-white/10 hover:text-white'
                    )}
                    aria-current={active ? 'page' : undefined}
                  >
                    <Icon className='h-[18px] w-[18px] shrink-0' aria-hidden='true' />
                    <span className={clsx('flex-1', isRTL ? 'text-right' : 'text-left')}>
                      {item.label}
                    </span>
                    {item.badge !== undefined ? (
                      <span
                        className={clsx(
                          'rounded-full px-2 py-0.5 text-[11px] font-bold',
                          active
                            ? 'bg-white/20 text-white'
                            : 'bg-secondary-700 text-secondary-200'
                        )}
                      >
                        {item.badge}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {sidebarFooter ? (
        <div className='border-t border-white/10 p-4'>{sidebarFooter}</div>
      ) : null}
    </div>
  );

  return (
    <div className='min-h-screen bg-brand-background text-secondary-950 dark:bg-secondary-950 dark:text-white'>
      <aside
        className={clsx(
          'fixed inset-y-0 z-40 hidden w-72 bg-sidebar lg:block',
          isRTL ? 'right-0' : 'left-0'
        )}
      >
        {sidebar}
      </aside>

      {mobileOpen ? (
        <div className='fixed inset-0 z-50 lg:hidden'>
          <button
            type='button'
            className='absolute inset-0 bg-secondary-950/60 backdrop-blur-sm'
            onClick={() => setMobileOpen(false)}
            aria-label='إغلاق القائمة'
          />
          <aside
            className={clsx(
              'absolute inset-y-0 w-[min(20rem,88vw)] bg-sidebar shadow-2xl',
              isRTL ? 'right-0' : 'left-0'
            )}
          >
            <button
              type='button'
              onClick={() => setMobileOpen(false)}
              className={clsx(
                'absolute top-5 z-10 rounded-lg p-2 text-secondary-300 hover:bg-white/10 hover:text-white',
                isRTL ? 'left-3' : 'right-3'
              )}
              aria-label={t('navigation:closeMenu')}
            >
              <X className='h-5 w-5' />
            </button>
            {sidebar}
          </aside>
        </div>
      ) : null}

      <div className={clsx('min-h-screen', isRTL ? 'lg:mr-72' : 'lg:ml-72')}>
        <header className='sticky top-0 z-30 border-b border-secondary-200 bg-white/95 backdrop-blur dark:border-secondary-800 dark:bg-secondary-900/95'>
          <div className='flex h-16 items-center gap-3 px-4 sm:px-6'>
            <button
              type='button'
              onClick={() => setMobileOpen(true)}
              className='rounded-lg p-2 text-secondary-600 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-800 lg:hidden'
              aria-label={t('navigation:openMenu')}
            >
              <Menu className='h-5 w-5' />
            </button>

            <div className='hidden flex-1 md:block' />

            <div className={clsx('flex items-center gap-1 sm:gap-2', isRTL ? 'mr-auto' : 'ml-auto')}>
              {actions}
              <ThemeToggle variant='minimal' size='md' />
              <LanguageToggle showLabel={false} variant='minimal' />
              <NotificationsBell
                className='rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800'
                onOpenCenter={
                  notificationsItem
                    ? () => handleNavigation(notificationsItem)
                    : undefined
                }
              />
              <div className='relative' ref={profileRef}>
                <button
                  type='button'
                  onClick={() => setProfileOpen(current => !current)}
                  className='hidden items-center gap-2 rounded-lg p-1.5 hover:bg-secondary-100 dark:hover:bg-secondary-800 sm:flex'
                  aria-expanded={profileOpen}
                >
                  <span className='flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 font-bold text-primary-700 dark:bg-primary-950 dark:text-primary-300'>
                    {userName.trim().charAt(0) || 'م'}
                  </span>
                  <ChevronDown className='h-4 w-4 text-secondary-400' />
                </button>

                {profileOpen ? (
                  <div
                    className={clsx(
                      'absolute top-full mt-2 w-64 overflow-hidden rounded-xl border border-secondary-200 bg-white shadow-xl dark:border-secondary-700 dark:bg-secondary-900',
                      isRTL ? 'left-0' : 'right-0'
                    )}
                  >
                    <div className='border-b border-secondary-100 px-4 py-3 dark:border-secondary-800'>
                      <p className='font-semibold text-secondary-900 dark:text-white'>
                        {userName}
                      </p>
                      {userEmail ? (
                        <p className='mt-0.5 truncate text-xs text-secondary-500'>
                          {userEmail}
                        </p>
                      ) : null}
                    </div>
                    <div className='p-2'>
                      <button
                        type='button'
                        onClick={() => navigate('/settings')}
                        className='flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-secondary-700 hover:bg-secondary-50 dark:text-secondary-200 dark:hover:bg-secondary-800'
                      >
                        <Settings className='h-4 w-4' />
                        {t('navigation:settings')}
                      </button>
                      {onLogout ? (
                        <button
                          type='button'
                          onClick={onLogout}
                          className='flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-danger-700 hover:bg-danger-50 dark:text-danger-300 dark:hover:bg-danger-950'
                        >
                          <LogOut className='h-4 w-4' />
                          {t('common:actions.logout')}
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </header>

        {notice ? <div className='px-4 pt-4 sm:px-6'>{notice}</div> : null}

        <main className='min-h-[calc(100vh-4rem)] bg-brand-background px-4 pb-24 pt-6 dark:bg-secondary-950 sm:px-6 lg:pb-8'>
          <div className='mx-auto max-w-[1600px]'>
            <div className='mb-6'>
              <h1 className='text-2xl font-bold tracking-tight text-secondary-950 dark:text-white'>
                {pageTitle}
              </h1>
              {pageDescription ? (
                <p className='mt-1 text-sm text-secondary-500 dark:text-secondary-400'>
                  {pageDescription}
                </p>
              ) : null}
            </div>
            {children}
          </div>
        </main>
      </div>

      <nav
        className='fixed inset-x-0 bottom-0 z-40 border-t border-secondary-200 bg-white/95 px-2 pb-[max(.5rem,env(safe-area-inset-bottom))] pt-2 backdrop-blur dark:border-secondary-800 dark:bg-secondary-900/95 lg:hidden'
        aria-label={t('navigation:mobileMenu')}
      >
        <div className='mx-auto grid max-w-lg grid-flow-col auto-cols-fr'>
          {mobileNavigation.map(item => {
            const Icon = item.icon;
            const active = item.id === activeItemId;
            return (
              <button
                type='button'
                key={item.id}
                onClick={() => handleNavigation(item)}
                className={clsx(
                  'flex min-h-12 flex-col items-center justify-center gap-1 rounded-lg px-1 text-[11px] font-semibold',
                  active
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-950 dark:text-primary-300'
                    : 'text-secondary-500 dark:text-secondary-400'
                )}
                aria-current={active ? 'page' : undefined}
              >
                <Icon className='h-5 w-5' />
                <span className='max-w-full truncate'>{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default AppShell;
